import * as joint from '@clientio/rappid';

import { FlowchartConfig } from '../base/flowchart.config';
import { ContainerAttributes } from '../operations/container/container-attributes';

const TITLE__DUMMY_BBOX_PADDING = 1;

export class HeaderElement extends joint.shapes.standard.Rectangle {
  parentContainer: joint.dia.Cell;

  constructor(
    parameters: joint.dia.Element.Attributes,
    private paper: joint.dia.Paper,
  ) {
    super(parameters);
  }

  defaults() {
    return {
      ...super.defaults,
      type: 'treetop.HeaderElement',
    };
  }

  initialize() {
    super.initialize();
    // Mark itself
    this.set(ContainerAttributes.IsHeader, true);
    this.on('change', this.onAttributeChange);
  }

  onAttributeChange() {
    // If parent changes (when a container uses this)
    if (this.changed.parent) {
      this.parentContainer = this.getParentCell();
      // Mark its parent.
      this.parentContainer.set(ContainerAttributes.HasHeader, true);
      // On parent size change
      this.parentContainer.on('change:size', () => {
        // On parent size change
        this.resizeShape();
      });

      // On all this.attribute changes (only this, no relation to parent)
      this.resizeShape();
    }

    // TODO: Consider setting label after creating the element? (To remove isHeader check)
    if (this.changed.isHeader || this.changed.attrs?.label) {
      this.flattenTitle();
      this.resizeShape();
    }
  }

  flattenTitle() {
    const { attributes } = this;
    const text = attributes.attrs.label.text;
    this.attr({
      label: {
        text: this.getFlatText(text),
      },
    });
  }

  resizeShape() {
    const dummyText = this.createDummyText();
    try {
      dummyText.appendTo(this.paper.svg);
      const dummyBbox = this.appendPaddingToDummyBbox(dummyText.getBBox());

      if (this.parentContainer) {
        const parentContainerWidth = this.parentContainer.attributes.size.width;
        if (dummyBbox.width > parentContainerWidth) {
          this.parentContainer.set('hasTwoLinesTitle', true);
          this.resizeTwoLines(parentContainerWidth as number);
          this.repositionForTwoLines();
        } else {
          this.resizeOneLine(
            dummyBbox.width < FlowchartConfig.containerNode.headerMin.width
              ? FlowchartConfig.containerNode.headerMin.width
              : dummyBbox.width,
          );
          this.parentContainer.set('hasTwoLinesTitle', false);
          this.repositionForOneLine();
        }
      } else {
        if (dummyBbox.width > FlowchartConfig.containerNode.minSize.width) {
          this.resizeTwoLines(FlowchartConfig.containerNode.headerSizeBig.width);
        } else {
          this.resizeOneLine(FlowchartConfig.containerNode.headerSize.width);
        }
      }
    } catch (error) {
      console.warn(error);
    } finally {
      // Remove the dummyText from the paper
      dummyText.remove();
    }
  }

  private getFlatText(text = ''): string {
    return text.replace(/\n|\r/g, ' ');
  }

  private appendPaddingToDummyBbox(bbox: joint.g.Rect): joint.g.Rect {
    bbox.width = Math.ceil(bbox.width) + TITLE__DUMMY_BBOX_PADDING;
    return bbox;
  }

  private createDummyText(): joint.VElement {
    const { attributes } = this;
    const text = attributes.attrs.label.text;
    // Create a dummy text, just to get the bbox information beforehand.
    const dummyText = joint.V('text');
    dummyText.text(this.getFlatText(text));
    // TODO: Make dummy attributes re-usable among future Shapes.
    dummyText.attr({
      'font-size': 14,
      'font-weight': 500,
      'font-family': "'Roboto', 'Helvetica Neue', 'sans-serif'",
    });

    return dummyText;
  }

  private resizeOneLine(width: number): void {
    this.resize(width + FlowchartConfig.containerNode.styles.headerTextPadding / 2, FlowchartConfig.containerNode.headerSize.height);
    this.attr({
      label: {
        textWrap: {
          width: null,
          ellipsis: false,
          maxLineCount: 1,
        },
        textVerticalAnchor: 'bottom',
        transform: 'translate(0px, 8px)',
      },
    });
    this.adjustTargetAnchor();
  }

  private resizeTwoLines(width: number): void {
    this.resize(width + FlowchartConfig.containerNode.styles.headerTextPadding / 2, FlowchartConfig.containerNode.headerSizeBig.height);
    this.attr({
      label: {
        textWrap: {
          width: -FlowchartConfig.containerNode.styles.headerTextPadding,
          ellipsis: true,
          maxLineCount: 2,
        },
        textVerticalAnchor: 'middle',
        transform: 'translate(0px, 0px)',
      },
    });
    this.adjustTargetAnchor();
  }

  private repositionForTwoLines() {
    if (this.parentContainer) {
      const parentContainerPosition = this.parentContainer.position();
      this.position(
        parentContainerPosition.x + FlowchartConfig.containerNode.styles.headerNegativePadding,
        parentContainerPosition.y +
          FlowchartConfig.containerNode.styles.headerNegativePadding -
          FlowchartConfig.containerNode.headerSizeBig.height +
          FlowchartConfig.containerNode.headerSize.height,
      );
    }
  }

  private repositionForOneLine() {
    if (this.parentContainer) {
      const parentContainerPosition = this.parentContainer.position();
      this.position(
        parentContainerPosition.x + FlowchartConfig.containerNode.styles.headerNegativePadding,
        parentContainerPosition.y + FlowchartConfig.containerNode.styles.headerNegativePadding,
      );
    }
  }

  private adjustTargetAnchor() {
    const linksToElement = this.graph.getLinks().filter((link) => {
      const target = link.getTargetElement();
      return target && target.id === this.parentContainer.id && link.get('target').anchor.name === 'top';
    });

    linksToElement.forEach((link) => {
      const target = link.getTargetElement();
      if (target) {
        const dy = target.attributes.hasTwoLinesTitle ? -FlowchartConfig.containerNode.headerSizeBig.padding : 0;
        const newTarget = {
          id: target.id,
          anchor: {
            name: 'top',
            args: { dy: dy },
          },
        };

        link.target(newTarget);
      }
    });
  }
}
